// CZ: hull deductible options of an insurer, maintained by the broker as the
// fleet vehicle default setting "hullDeductibleOptions":
//   attributeValue = '[{"percent":10,"min":5000,"max":50000},{"min":10000},...]'
//   settingCriteria = {} (the options apply to all vehicles of the insurer)
// An option has a percent and / or a min. and / or a max. amount (CZK), at
// least one of them. The client picks one of them for the requested Hull; the
// coverage request (clientCoveragesInfo) stores it as
// "{percent}%/min: {min} CZK/max: {max} CZK" (without the undefined parts).

import { FleetDefaultSetting } from "types/types";
import {
  findApplicableClientOptionSetting,
  parseClientOptionList,
} from "./clientOptionSettings";

export const HULL_DEDUCTIBLE_OPTIONS_ATTRIBUTE_NAME = "hullDeductibleOptions";

export interface HullDeductibleOption {
  percent?: number | null;
  min?: number | null;
  max?: number | null;
}

const isSet = (v: any) => v !== null && v !== undefined;
const isValidPart = (v: any, isAmount: boolean) =>
  !isSet(v) ||
  (typeof v === "number" &&
    v > 0 &&
    (isAmount ? Number.isInteger(v) : v <= 100));

/** The options of a setting's attributeValue; [] if it is not such a JSON list. */
export const parseHullDeductibleOptions = (
  attributeValue: string | number | null | undefined,
): HullDeductibleOption[] =>
  parseClientOptionList(attributeValue).filter(
    (o) =>
      o &&
      (isSet(o.percent) || isSet(o.min) || isSet(o.max)) &&
      isValidPart(o.percent, false) &&
      isValidPart(o.min, true) &&
      isValidPart(o.max, true),
  );

/**
 * "{percent}%/min: {min} CZK/max: {max} CZK" without the undefined parts -
 * the display and the stored format.
 */
export const formatHullDeductibleOption = (
  option: HullDeductibleOption,
): string =>
  [
    isSet(option.percent) ? `${option.percent}%` : null,
    isSet(option.min) ? `min: ${option.min} CZK` : null,
    isSet(option.max) ? `max: ${option.max} CZK` : null,
  ]
    .filter(Boolean)
    .join("/");

/**
 * The deductible options the client can pick for a Hull requested from
 * `coverageFrom` ("YYYY-MM-DD"): those of the insurer's "hullDeductibleOptions"
 * setting with the latest valid from date that is not after the coverage
 * start. [] when there is no insurer, no coverage start or no such setting.
 */
export const getHullDeductibleOptions = (
  fleetDefaultSettings: FleetDefaultSetting[] | null | undefined,
  insurerPartnerNumber: number | string | null | undefined,
  coverageFrom: string | null | undefined,
): string[] => {
  const applicable = findApplicableClientOptionSetting(
    fleetDefaultSettings,
    HULL_DEDUCTIBLE_OPTIONS_ATTRIBUTE_NAME,
    insurerPartnerNumber,
    coverageFrom,
  );
  if (!applicable) return [];
  return Array.from(
    new Set(
      parseHullDeductibleOptions(applicable.attributeValue).map(
        formatHullDeductibleOption,
      ),
    ),
  );
};
